// frontend/src/pages/partner/ApiPartner.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './ApiPartner.css';

const ApiPartner = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showSecret, setShowSecret] = useState({});
  const [activeTab, setActiveTab] = useState('keys');
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchLogs();
  }, []);

  useEffect(() => {
    if (selectedService) {
      fetchApiKeys(selectedService.id);
    }
  }, [selectedService]);

  const fetchServices = async () => {
    try {
      const res = await API.get('/companies/my');
      setServices(res.data.companies || []);
      if (res.data.companies?.length > 0) {
        setSelectedService(res.data.companies[0]);
      }
    } catch (err) {
      console.error('Erreur chargement services:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeys = async (companyId) => {
    try {
      const res = await API.get(`/api-keys/list/${companyId}`);
      setApiKeys(res.data.apiKeys || []);
    } catch (err) {
      console.error('Erreur chargement clés API:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await API.get('/api-keys/logs?limit=20');
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Erreur chargement logs:', err);
    }
  };

  const generateApiKey = async () => {
    if (!selectedService) {
      setMsg({ type: 'error', text: 'Veuillez sélectionner un service' });
      return;
    }

    setGenerating(true);
    try {
      await API.post('/api-keys/generate', {
        company_id: selectedService.id
      });

      setMsg({ type: 'success', text: '✅ Nouvelle clé API générée avec succès' });
      fetchApiKeys(selectedService.id);

      setTimeout(() => setMsg(null), 5000);
    } catch (err) {
      setMsg({ 
        type: 'error', 
        text: err.response?.data?.error || 'Erreur lors de la génération' 
      });
    } finally {
      setGenerating(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir révoquer cette clé ? Cette action est irréversible.')) {
      return;
    }

    try {
      await API.delete(`/api-keys/revoke/${keyId}`);
      setMsg({ type: 'success', text: '✅ Clé API révoquée' });
      fetchApiKeys(selectedService.id);
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ type: 'error', text: 'Erreur lors de la révocation' });
    }
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    setMsg({ type: 'success', text: '📋 Copié dans le presse-papier' });
    setTimeout(() => {
      setCopiedKey(null);
      setMsg(null);
    }, 2000);
  };

  const toggleSecret = (keyId) => {
    setShowSecret(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active': 
        return <span className="badge-pro badge-success"><i className="fas fa-check-circle"></i> Active</span>;
      case 'revoked': 
        return <span className="badge-pro badge-danger"><i className="fas fa-ban"></i> Révoquée</span>;
      default: 
        return <span className="badge-pro badge-secondary">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="loading-container-pro">
        <div className="loader-pro"></div>
        <p>Chargement de votre espace API...</p>
      </div>
    );
  }

  return (
    <div className="api-partner-pro">
      {/* ── Hero ── */}
      <motion.div
        className="ap-hero"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <div className="ap-hero-orb ap-hero-orb-1" />
        <div className="ap-hero-orb ap-hero-orb-2" />
        <div className="ap-hero-left">
          <div className="ap-hero-icon"><i className="fas fa-code" /></div>
          <div>
            <h1>API & Intégration</h1>
            <p>Gérez vos clés d'API et surveillez l'utilisation de vos services</p>
          </div>
        </div>
        <div style={{ zIndex: 1 }}>
          <div className="ap-stat-pill">
            <i className="fas fa-key" />
            <span>{apiKeys.length} clé{apiKeys.length !== 1 ? 's' : ''} active{apiKeys.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </motion.div>

      {/* Messages */}
      <AnimatePresence>
        {msg && (
          <motion.div 
            className={`alert-pro ${msg.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="tabs-container-pro">
        <motion.button 
          className={`tab-btn-pro ${activeTab === 'keys' ? 'active' : ''}`}
          onClick={() => setActiveTab('keys')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <i className="fas fa-key"></i>
          <span>Clés API</span>
        </motion.button>
        <motion.button 
          className={`tab-btn-pro ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <i className="fas fa-history"></i>
          <span>Logs d'utilisation</span>
        </motion.button>
        <motion.button 
          className={`tab-btn-pro ${activeTab === 'docs' ? 'active' : ''}`}
          onClick={() => setActiveTab('docs')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <i className="fas fa-book"></i>
          <span>Documentation</span>
        </motion.button>
      </div>

      {/* Contenu des tabs */}
      <div className="tab-content-pro">
        {/* TAB 1: Clés API */}
        <AnimatePresence mode="wait">
          {activeTab === 'keys' && (
            <motion.div 
              key="keys"
              className="keys-tab-pro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Sélecteur de service */}
              {services.length > 0 && (
                <div className="service-selector-pro">
                  <label>
                    <i className="fas fa-cube"></i>
                    Sélectionnez un service
                  </label>
                  <select
                    value={selectedService?.id || ''}
                    onChange={(e) => {
                      const service = services.find(s => s.id === parseInt(e.target.value));
                      setSelectedService(service);
                    }}
                  >
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedService && (
                <>
                  {/* Bouton génération */}
                  <motion.div 
                    className="generate-section-pro"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <button 
                      className="btn-generate-pro"
                      onClick={generateApiKey}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus-circle"></i>
                          Générer une nouvelle clé API
                        </>
                      )}
                    </button>
                    <p className="generate-hint">
                      <i className="fas fa-shield-alt"></i>
                      Une clé API permet à votre service d'interagir avec POINTPAY de manière sécurisée.
                    </p>
                  </motion.div>

                  {/* Liste des clés */}
                  <div className="keys-list-pro">
                    <h3>
                      <i className="fas fa-key"></i>
                      Clés API {apiKeys.length > 0 && `(${apiKeys.length})`}
                    </h3>
                    
                    {apiKeys.length === 0 ? (
                      <motion.div 
                        className="empty-keys-pro"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <i className="fas fa-key"></i>
                        <h4>Aucune clé API</h4>
                        <p>Générez votre première clé pour commencer à intégrer votre service</p>
                      </motion.div>
                    ) : (
                      <div className="keys-grid-pro">
                        {apiKeys.map((key, index) => (
                          <motion.div 
                            key={key.id} 
                            className={`key-card-pro ${key.status}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ y: -2 }}
                          >
                            <div className="key-card-header">
                              <div className="key-icon">
                                <i className="fas fa-key"></i>
                              </div>
                              <div className="key-status-badge">
                                {getStatusBadge(key.status)}
                              </div>
                            </div>

                            <div className="key-field">
                              <span className="field-label">
                                <i className="fas fa-id-card"></i>
                                Clé publique
                              </span>
                              <div className="field-value">
                                <code>{key.key_value}</code>
                                <motion.button 
                                  className={`copy-btn ${copiedKey === key.id ? 'copied' : ''}`}
                                  onClick={() => copyToClipboard(key.key_value, key.id)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  {copiedKey === key.id ? (
                                    <i className="fas fa-check"></i>
                                  ) : (
                                    <i className="fas fa-copy"></i>
                                  )}
                                </motion.button>
                              </div>
                            </div>

                            {key.secret_value && (
                              <div className="key-field secret">
                                <span className="field-label">
                                  <i className="fas fa-lock"></i>
                                  Clé secrète
                                </span>
                                <div className="field-value">
                                  <code>
                                    {showSecret[key.id] ? key.secret_value : '••••••••••••••••••••••••••••••••'}
                                  </code>
                                  <div className="field-actions">
                                    <motion.button 
                                      className="action-btn"
                                      onClick={() => toggleSecret(key.id)}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      title={showSecret[key.id] ? 'Masquer' : 'Afficher'}
                                    >
                                      <i className={`fas fa-eye${showSecret[key.id] ? '' : '-slash'}`}></i>
                                    </motion.button>
                                    <motion.button 
                                      className="action-btn"
                                      onClick={() => copyToClipboard(key.secret_value, `${key.id}-secret`)}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      title="Copier"
                                    >
                                      <i className="fas fa-copy"></i>
                                    </motion.button>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="key-footer">
                              <span>
                                <i className="far fa-calendar-alt"></i>
                                Créée le {new Date(key.created_at).toLocaleDateString('fr-FR', { 
                                  day: 'numeric', month: 'long', year: 'numeric' 
                                })}
                              </span>
                              {key.last_used_at && (
                                <span>
                                  <i className="far fa-clock"></i>
                                  Dernière utilisation: {new Date(key.last_used_at).toLocaleString('fr-FR')}
                                </span>
                              )}
                            </div>

                            {key.status === 'active' && (
                              <motion.button 
                                className="revoke-btn"
                                onClick={() => revokeApiKey(key.id)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <i className="fas fa-trash-alt"></i>
                                Révoquer
                              </motion.button>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {services.length === 0 && (
                <motion.div 
                  className="empty-services-pro"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <i className="fas fa-cube"></i>
                  <h3>Aucun service</h3>
                  <p>Créez d'abord un service pour générer des clés API</p>
                  <button 
                    className="btn-primary-pro"
                    onClick={() => navigate('/dashboard/partner/services')}
                  >
                    <i className="fas fa-plus"></i>
                    Créer un service
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* TAB 2: Logs d'utilisation */}
          {activeTab === 'logs' && (
            <motion.div 
              key="logs"
              className="logs-tab-pro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="logs-header">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Derniers appels API
                </h3>
                {logs.length > 0 && (
                  <span className="logs-count">{logs.length} appels</span>
                )}
              </div>

              {logs.length === 0 ? (
                <div className="empty-logs-pro">
                  <i className="fas fa-chart-line"></i>
                  <h4>Aucun appel API</h4>
                  <p>Les appels API apparaîtront ici une fois que vous aurez intégré vos clés</p>
                </div>
              ) : (
                <div className="logs-table-pro">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Service</th>
                        <th>Endpoint</th>
                        <th>Méthode</th>
                        <th>Statut</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, index) => (
                        <motion.tr 
                          key={log.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <td className="date-cell">
                            {new Date(log.created_at).toLocaleString('fr-FR')}
                          </td>
                          <td>{log.company_name}</td>
                          <td><code>{log.endpoint}</code></td>
                          <td>
                            <span className={`method-pro ${log.method?.toLowerCase()}`}>
                              {log.method}
                            </span>
                          </td>
                          <td>
                            <span className={`status-code-pro ${log.response_code < 300 ? 'success' : log.response_code < 400 ? 'warning' : 'error'}`}>
                              {log.response_code}
                            </span>
                          </td>
                          <td>{log.ip_address}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {/* TAB 3: Documentation */}
          {activeTab === 'docs' && (
            <motion.div 
              key="docs"
              className="docs-tab-pro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="docs-intro">
                <i className="fas fa-book-open"></i>
                <h3>Documentation API</h3>
                <p>Intégrez POINTPAY à votre application en quelques minutes</p>
              </div>

              {/* Authentification */}
              <div className="doc-card">
                <h4>
                  <i className="fas fa-lock"></i>
                  Authentification
                </h4>
                <p>Toutes les requêtes API doivent inclure vos clés dans les en-têtes :</p>
                <pre className="code-block">
                  <code>{`X-API-Key: pk_votre_clé_publique
X-API-Secret: sk_votre_clé_secrète`}</code>
                </pre>
              </div>

              {/* Endpoints */}
              <div className="doc-card">
                <h4>
                  <i className="fas fa-plug"></i>
                  Endpoints disponibles
                </h4>

                <div className="endpoint-list">
                  <div className="endpoint-item">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-details">
                      <code>/api/balance?user_id={'{id}'}</code>
                      <p>Vérifie le solde de points d'un client</p>
                      <details>
                        <summary>Exemple de réponse</summary>
                        <pre>{`{
  "success": true,
  "balance": 1250,
  "currency": "points"
}`}</pre>
                      </details>
                    </div>
                  </div>

                  <div className="endpoint-item">
                    <div className="endpoint-method post">POST</div>
                    <div className="endpoint-details">
                      <code>/api/consume</code>
                      <p>Débite des points du compte d'un client</p>
                      <details>
                        <summary>Corps de la requête</summary>
                        <pre>{`{
  "user_id": 123,
  "points": 5,
  "idempotency_key": "unique_123456"
}`}</pre>
                      </details>
                    </div>
                  </div>

                  <div className="endpoint-item">
                    <div className="endpoint-method post">POST</div>
                    <div className="endpoint-details">
                      <code>/api/credit</code>
                      <p>Crédite des points sur le compte d'un client</p>
                      <details>
                        <summary>Corps de la requête</summary>
                        <pre>{`{
  "user_id": 123,
  "points": 100,
  "reason": "gift"
}`}</pre>
                      </details>
                    </div>
                  </div>

                  <div className="endpoint-item">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-details">
                      <code>/api/transactions?user_id={'{id}'}</code>
                      <p>Liste les transactions d'un client</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exemples d'intégration */}
              <div className="doc-card">
                <h4>
                  <i className="fas fa-code"></i>
                  Exemples d'intégration
                </h4>
                
                <div className="code-examples">
                  <div className="example-tabs">
                    <button className="example-tab active">cURL</button>
                    <button className="example-tab">JavaScript</button>
                    <button className="example-tab">Python</button>
                    <button className="example-tab">PHP</button>
                  </div>
                  <pre className="code-block">
                    <code>{`curl -X GET "https://proxy-pay.runit-dev.fr/api/balance?user_id=123" \\
  -H "X-API-Key: pk_votre_clé" \\
  -H "X-API-Secret: sk_votre_secret"`}</code>
                  </pre>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ApiPartner;